import * as fs from 'fs';
import * as path from 'path';
import type { Survey, SurveyQuestion } from '../fetchSurvey/types/survey';
import type {
  LogicFilterGroup,
  LogicFilterLeaf,
  LogicFilterNode,
} from '../fetchSurvey/types/logic';
import { QUESTION_TYPE } from '../fetchSurvey/constants/questionTypes';
import {
  detectFramework,
  resolveSdkPackageName,
  type AgentFramework,
} from './detect-framework';
import { extractSurveyTypesFromSurvey } from './fetch-survey-inventory';

const PLACEHOLDER_RE = /\{\{([^}]+)\}\}/g;
const CUSTOM_FIELD_ID_RE = /_c_[a-f0-9]+/gi;

export interface FrameworkSection {
  framework: AgentFramework;
  packageName: string | undefined;
  frameworkLine: string;
  packageLine: string;
}

export interface ComponentInventoryRow {
  componentFile: string;
  requiredIf: string;
  blocking?: boolean;
}

export interface MatrixVariantRow {
  questionId: string;
  type: string;
  displayStyle: string;
  gridLayout: string;
  statementLayout: string;
  transposeTable: string;
  selectionMode: string;
  hasNotApplicableOption: string;
  branchOn: string;
  scaleAnchorLabelCount: string;
  matrixFormat: string;
  nonNaM: string;
}

export interface McqOtherInventoryRow {
  questionId: string;
  optionId: string;
  contentType: string;
  countryCodes: string;
}

export interface RankOrderVariantRow {
  questionId: string;
  interactionMode: string;
  optionDisplay: string;
  branchOn: string;
}

export interface MediaInventoryRow {
  questionId: string;
  type: string;
  hasContainerMedia: string;
  alignment: string;
  size: string;
  attachmentCount: string;
  enablePreview: string;
  enableDownload: string;
}

export interface CustomFieldRow {
  id: string;
  usedIn: string;
}

export interface PlaceholderRow {
  token: string;
  key: string;
  questionIds: string;
}

export interface LogicInventoryRow {
  questionId: string;
  skipLogics: string;
  displayLogic: string;
  answerLogic: string;
  customFieldRefs: string;
}

function isLogicGroup(node: LogicFilterNode): node is LogicFilterGroup {
  return 'filters' in node && Array.isArray((node as LogicFilterGroup).filters);
}

function collectCustomFieldIdsFromLogic(nodes: LogicFilterNode[] | undefined, out: Set<string>): void {
  if (!nodes) return;
  for (const node of nodes) {
    if (isLogicGroup(node)) {
      collectCustomFieldIdsFromLogic(node.filters, out);
      continue;
    }
    const leaf = node as LogicFilterLeaf;
    const entity = leaf.details?.conditionEntity;
    if (entity?.entityType === 'CUSTOM_FIELD') {
      out.add(entity.id);
    }
  }
}

function collectCustomFieldIdsFromText(text: string | undefined, out: Set<string>): void {
  if (!text) return;
  const matches = text.match(CUSTOM_FIELD_ID_RE);
  if (matches) {
    for (const m of matches) out.add(m.toLowerCase() === m ? m : m);
  }
}

function hasAnswerLogicOnQuestion(question: SurveyQuestion): boolean {
  if (question.type === QUESTION_TYPE.MCQ || question.type === QUESTION_TYPE.RANK_ORDER) {
    if (question.options?.some(o => o.answerLogic)) return true;
  }
  if ('statementRows' in question && question.statementRows?.some(r => r.answerLogic)) return true;
  if ('scaleColumns' in question && question.scaleColumns?.some(c => c.answerLogic)) return true;
  return false;
}

function matrixDisplayStyle(question: SurveyQuestion): string {
  if (question.type === QUESTION_TYPE.CFM_MATRIX) {
    return '—';
  }
  if ('displayStyle' in question && question.displayStyle) {
    return String(question.displayStyle);
  }
  return '—';
}

function matrixNonNaCount(question: SurveyQuestion): string {
  if (!('scaleColumns' in question) || !Array.isArray(question.scaleColumns)) return '—';
  return String(question.scaleColumns.filter(col => col.id !== 'na').length);
}

function matrixFormatLabel(question: SurveyQuestion): string {
  if (question.type !== QUESTION_TYPE.CFM_MATRIX) return '—';
  if ('matrixFormat' in question && question.matrixFormat) {
    return String(question.matrixFormat);
  }
  return '—';
}

/** Human-readable branch identifier for §3c branchOn column and layout-tasks labels. */
export function formatMatrixBranchOn(
  row: Pick<
    MatrixVariantRow,
    'type' | 'displayStyle' | 'gridLayout' | 'statementLayout' | 'transposeTable' | 'matrixFormat'
  >,
): string {
  const parts: string[] = [];

  if (row.type === 'CFM_MATRIX') {
    if (row.gridLayout && row.gridLayout !== '—' && row.gridLayout !== 'standard') {
      parts.push(`question.gridLayout=${row.gridLayout}`);
    }
    if (/bipolar/i.test(row.statementLayout)) {
      parts.push('question.statementLayout=bipolar');
      if (row.matrixFormat && row.matrixFormat !== '—') {
        parts.push(`question.matrixFormat=${row.matrixFormat}`);
      }
    }
    if (/true/i.test(row.transposeTable)) {
      parts.push('question.transposeTable=true');
    }
    if (!parts.length) {
      parts.push('question.gridLayout=standard');
    }
    return parts.join(', ');
  }

  if (row.type === 'SLIDER_MATRIX') {
    if (row.gridLayout && row.gridLayout !== '—' && row.gridLayout !== 'standard') {
      parts.push(`question.gridLayout=${row.gridLayout}`);
    }
    return parts.length ? parts.join(', ') : 'question.gridLayout=standard';
  }

  if (row.displayStyle && row.displayStyle !== '—' && row.displayStyle !== 'standard') {
    parts.push(`question.displayStyle=${row.displayStyle}`);
  }
  if (row.gridLayout && row.gridLayout !== '—' && row.gridLayout !== 'standard') {
    parts.push(`question.gridLayout=${row.gridLayout}`);
  }
  if (!parts.length) {
    parts.push(
      row.displayStyle && row.displayStyle !== '—'
        ? `question.displayStyle=${row.displayStyle}`
        : 'question.displayStyle=standard',
    );
  }
  return parts.join(', ');
}

const COMPONENT_BY_TYPE: Record<string, { file: string; label: string; blocking?: boolean }[]> = {
  [QUESTION_TYPE.CFM_MATRIX]: [{ file: 'LikertMatrixScale.tsx', label: 'CFM_MATRIX' }],
  [QUESTION_TYPE.CSAT_MATRIX]: [{ file: 'CsatMatrixScale.tsx', label: 'CSAT_MATRIX or RATING_MATRIX' }],
  [QUESTION_TYPE.RATING_MATRIX]: [{ file: 'CsatMatrixScale.tsx', label: 'CSAT_MATRIX or RATING_MATRIX' }],
  [QUESTION_TYPE.SLIDER_MATRIX]: [
    { file: 'SliderMatrixScale.tsx', label: 'SLIDER_MATRIX', blocking: true },
    { file: 'CustomSliderTrack.tsx', label: 'SLIDER_MATRIX or matrix graphics' },
  ],
  [QUESTION_TYPE.FILE_UPLOAD]: [
    { file: 'FileUploadScale.tsx', label: 'FILE_UPLOAD', blocking: true },
  ],
  [QUESTION_TYPE.NPS_SCALE]: [{ file: 'RatingScale.tsx', label: 'NPS_SCALE' }],
  [QUESTION_TYPE.HEATMAP]: [
    { file: 'HeatmapScale.tsx', label: 'HEATMAP' },
    { file: 'lib/surveyUi/heatmapCoords.ts', label: 'HEATMAP' },
  ],
  [QUESTION_TYPE.RANK_ORDER]: [{ file: 'RankOrderScale.tsx', label: 'RANK_ORDER' }],
};

export function surveyHasMcqOtherOption(survey: Survey): boolean {
  for (const page of survey.pages) {
    for (const question of page.questions) {
      if (question.type !== QUESTION_TYPE.MCQ || !('options' in question)) continue;
      if (question.options?.some((o) => o.otherOption)) return true;
    }
  }
  return false;
}

const MCQ_OTHER_PICKER_CONTENT_TYPES = new Set(['PHONE_NUMBER', 'POSTAL_CODE', 'DATE']);

export function surveyHasMcqOtherPickerTypes(survey: Survey): boolean {
  for (const page of survey.pages) {
    for (const question of page.questions) {
      if (question.type !== QUESTION_TYPE.MCQ || !('options' in question)) continue;
      for (const option of question.options ?? []) {
        const contentType = option.otherOption?.contentType;
        if (contentType && MCQ_OTHER_PICKER_CONTENT_TYPES.has(contentType)) return true;
      }
    }
  }
  return false;
}

export function extractFrameworkSection(projectRoot: string): FrameworkSection {
  const pkgPath = path.join(projectRoot, 'package.json');
  const pkg = JSON.parse(fs.readFileSync(pkgPath, 'utf8')) as {
    dependencies?: Record<string, string>;
    devDependencies?: Record<string, string>;
  };
  const framework = detectFramework(pkg);
  const packageName = resolveSdkPackageName(pkg);

  const checks: Record<AgentFramework, string> = {
    nextjs: '[x] Next.js App Router  [ ] Vite  [ ] CRA  [ ] Existing app',
    vite: '[ ] Next.js App Router  [x] Vite  [ ] CRA  [ ] Existing app',
    cra: '[ ] Next.js App Router  [ ] Vite  [x] CRA  [ ] Existing app',
    unknown: '[ ] Next.js App Router  [ ] Vite  [ ] CRA  [x] Existing app',
  };

  return {
    framework,
    packageName,
    frameworkLine: `- **Framework:** ${checks[framework]}`,
    packageLine: `- **Package import name:** ${packageName ?? '(add @explorer02/cfm-survey-sdk or @repo/sdk)'}`,
  };
}

export function extractComponentInventory(survey: Survey): ComponentInventoryRow[] {
  const types = new Set(extractSurveyTypesFromSurvey(survey));
  const rows: ComponentInventoryRow[] = [
    { componentFile: 'IntroPage.tsx', requiredIf: 'intro page' },
    { componentFile: 'EndPage.tsx', requiredIf: 'end pages' },
    { componentFile: 'PausedPage.tsx', requiredIf: survey.pausedPage ? 'paused flag' : 'paused flag (if enabled)' },
    {
      componentFile: 'QuotaPage.tsx',
      requiredIf: survey.quotaFulfilledPage ? 'quota fulfilled page' : 'quota page (if configured)',
    },
  ];

  const seen = new Set<string>();
  for (const type of types) {
    const entries = COMPONENT_BY_TYPE[type];
    if (!entries) continue;
    for (const entry of entries) {
      const key = `${entry.file}:${entry.label}`;
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push({
        componentFile: entry.file,
        requiredIf: entry.label,
        blocking: entry.blocking,
      });
    }
  }

  if (types.has(QUESTION_TYPE.NPS_SCALE) || [...types].some((t) => t.includes('MATRIX'))) {
    rows.push({
      componentFile: 'lib/surveyUi/scaleValues.ts + getAccentColor.ts',
      requiredIf: 'NPS/matrix colors',
    });
  }
  if (types.has(QUESTION_TYPE.CSAT_MATRIX) || types.has(QUESTION_TYPE.RATING_MATRIX)) {
    rows.push({ componentFile: 'lib/surveyUi/icons.tsx', requiredIf: 'CSAT emoji/star' });
  }

  const hasDropdownMatrix = survey.pages.some((p) =>
    p.questions.some(
      (q) => 'gridLayout' in q && q.gridLayout === 'dropdown',
    ),
  );
  if (hasDropdownMatrix) {
    rows.push({ componentFile: 'MatrixDropdown.tsx', requiredIf: 'matrix dropdown/selectbox' });
  }

  return rows;
}

export function extractMatrixVariants(survey: Survey): MatrixVariantRow[] {
  const rows: MatrixVariantRow[] = [];
  const matrixTypes = [
    QUESTION_TYPE.CFM_MATRIX,
    QUESTION_TYPE.CSAT_MATRIX,
    QUESTION_TYPE.RATING_MATRIX,
    QUESTION_TYPE.SLIDER_MATRIX,
  ] as const;

  for (const page of survey.pages) {
    for (const question of page.questions) {
      if (!matrixTypes.includes(question.type as (typeof matrixTypes)[number])) continue;
      rows.push({
        questionId: question.id,
        type: question.type,
        displayStyle: matrixDisplayStyle(question),
        gridLayout:
          'gridLayout' in question && question.gridLayout ? String(question.gridLayout) : '—',
        statementLayout: 'statementLayout' in question ? String(question.statementLayout) : '—',
        transposeTable: 'transposeTable' in question ? String(question.transposeTable) : '—',
        selectionMode: 'selectionMode' in question ? String(question.selectionMode) : '—',
        hasNotApplicableOption:
          'hasNotApplicableOption' in question ? String(question.hasNotApplicableOption) : '—',
        branchOn: '',
        scaleAnchorLabelCount:
          'scaleAnchorLabels' in question && question.scaleAnchorLabels?.length
            ? String(question.scaleAnchorLabels.length)
            : '—',
        matrixFormat: matrixFormatLabel(question),
        nonNaM: matrixNonNaCount(question),
      });
    }
  }
  for (const row of rows) {
    row.branchOn = formatMatrixBranchOn(row);
  }
  return rows;
}

export function extractRankOrderVariants(survey: Survey): RankOrderVariantRow[] {
  const rows: RankOrderVariantRow[] = [];

  for (const page of survey.pages) {
    for (const question of page.questions) {
      if (question.type !== QUESTION_TYPE.RANK_ORDER) continue;
      rows.push({
        questionId: question.id,
        interactionMode: question.interactionMode,
        optionDisplay: question.optionDisplay,
        branchOn: '',
      });
    }
  }

  for (const row of rows) {
    row.branchOn = formatRankOrderBranchOn(row);
  }

  return rows;
}

function formatRankOrderBranchOn(
  row: Pick<RankOrderVariantRow, 'interactionMode' | 'optionDisplay'>,
): string {
  const parts = [
    `question.interactionMode=${row.interactionMode}`,
    `question.optionDisplay=${row.optionDisplay}`,
  ];
  return parts.join(', ');
}

export function formatRankOrderTableRows(rows: RankOrderVariantRow[]): string {
  if (!rows.length) {
    return '| (no rank order questions in fetch) | | | |';
  }
  return rows
    .map(
      (r) =>
        `| ${r.questionId} | ${r.interactionMode} | ${r.optionDisplay} | ${r.branchOn} |`,
    )
    .join('\n');
}

function readShellMediaFields(question: SurveyQuestion): {
  hasContainerMedia: boolean;
  alignment: string;
  size: string;
  attachmentCount: number;
  enablePreview: boolean;
  enableDownload: boolean;
} {
  const record = question as SurveyQuestion & {
    containerMedia?: { url?: string; alignment?: string; size?: number };
    mediaAttachments?: unknown[];
    enablePreview?: boolean;
    enableDownload?: boolean;
  };

  const hasContainerMedia = Boolean(record.containerMedia?.url);
  const attachmentCount = record.mediaAttachments?.length ?? 0;

  if (!hasContainerMedia && attachmentCount === 0 && !record.enablePreview && !record.enableDownload) {
    return {
      hasContainerMedia: false,
      alignment: '—',
      size: '—',
      attachmentCount: 0,
      enablePreview: false,
      enableDownload: false,
    };
  }

  return {
    hasContainerMedia,
    alignment: record.containerMedia?.alignment ?? '—',
    size:
      record.containerMedia?.size !== undefined ? String(record.containerMedia.size) : '—',
    attachmentCount,
    enablePreview: record.enablePreview === true,
    enableDownload: record.enableDownload === true,
  };
}

export function extractMediaInventory(survey: Survey): MediaInventoryRow[] {
  const rows: MediaInventoryRow[] = [];

  for (const page of survey.pages) {
    for (const question of page.questions) {
      const media = readShellMediaFields(question);
      if (
        !media.hasContainerMedia &&
        media.attachmentCount === 0 &&
        !media.enablePreview &&
        !media.enableDownload
      ) {
        continue;
      }

      rows.push({
        questionId: question.id,
        type: question.type,
        hasContainerMedia: media.hasContainerMedia ? 'yes' : 'no',
        alignment: media.alignment,
        size: media.size,
        attachmentCount: String(media.attachmentCount),
        enablePreview: media.enablePreview ? 'true' : 'false',
        enableDownload: media.enableDownload ? 'true' : 'false',
      });
    }
  }

  return rows;
}

export function formatMediaTableRows(rows: MediaInventoryRow[]): string {
  if (!rows.length) {
    return '| (no question media in fetch) | | | | | | | |';
  }
  return rows
    .map(
      (r) =>
        `| ${r.questionId} | ${r.type} | ${r.hasContainerMedia} | ${r.alignment} | ${r.size} | ${r.attachmentCount} | ${r.enablePreview} | ${r.enableDownload} |`,
    )
    .join('\n');
}

export function extractMcqOtherInventory(survey: Survey): McqOtherInventoryRow[] {
  const rows: McqOtherInventoryRow[] = [];
  for (const page of survey.pages) {
    for (const question of page.questions) {
      if (question.type !== QUESTION_TYPE.MCQ || !('options' in question)) continue;
      for (const option of question.options ?? []) {
        if (!option.otherOption) continue;
        const contentType = option.otherOption.contentType ?? 'TEXT';
        rows.push({
          questionId: question.id,
          optionId: option.id,
          contentType,
          countryCodes:
            option.otherOption.countryCodes?.length
              ? option.otherOption.countryCodes.join(', ')
              : '—',
        });
      }
    }
  }
  return rows;
}

export function extractCustomFieldInventory(survey: Survey): CustomFieldRow[] {
  const idToUses = new Map<string, Set<string>>();

  const add = (id: string, use: string) => {
    const normalized = id.startsWith('_c_') ? id : `_c_${id.replace(/^_c_/, '')}`;
    const set = idToUses.get(normalized) ?? new Set<string>();
    set.add(use);
    idToUses.set(normalized, set);
  };

  const scanLogic = (nodes: LogicFilterNode[] | undefined, use: string) => {
    const ids = new Set<string>();
    collectCustomFieldIdsFromLogic(nodes, ids);
    for (const id of ids) add(id, use);
  };

  for (const page of survey.pages) {
    for (const question of page.questions) {
      if (question.skipLogics?.length) {
        for (const sl of question.skipLogics) scanLogic(sl.condition.filters, 'skip');
      }
      if (question.displayLogic) scanLogic(question.displayLogic.condition.filters, 'display');
      if (question.displayVariants?.length) {
        for (const v of question.displayVariants) {
          if (v.displayLogic) scanLogic(v.displayLogic.condition.filters, 'display-variant');
          collectCustomFieldIdsFromText(v.questionText, new Set());
          const textIds = new Set<string>();
          collectCustomFieldIdsFromText(v.questionText, textIds);
          for (const id of textIds) add(id, 'copy');
        }
      }
      collectCustomFieldIdsFromText(question.questionText, new Set());
      const copyIds = new Set<string>();
      collectCustomFieldIdsFromText(question.questionText, copyIds);
      collectCustomFieldIdsFromText(question.questionDescription, copyIds);
      for (const id of copyIds) add(id, 'copy');

      if (question.type === QUESTION_TYPE.MCQ || question.type === QUESTION_TYPE.RANK_ORDER) {
        for (const opt of question.options) {
          if (opt.answerLogic) scanLogic(opt.answerLogic.condition.filters, 'answer');
        }
      }
      if ('statementRows' in question) {
        for (const row of question.statementRows) {
          if (row.answerLogic) scanLogic(row.answerLogic.condition.filters, 'answer');
        }
      }
      if ('scaleColumns' in question) {
        for (const col of question.scaleColumns) {
          if (col.answerLogic) scanLogic(col.answerLogic.condition.filters, 'answer');
        }
      }
    }
  }

  for (const endPage of survey.endPages) {
    if (endPage.displayLogic) scanLogic(endPage.displayLogic.condition.filters, 'end');
    const copyIds = new Set<string>();
    collectCustomFieldIdsFromText(endPage.header, copyIds);
    collectCustomFieldIdsFromText(endPage.description, copyIds);
    for (const id of copyIds) add(id, 'copy');
  }

  return [...idToUses.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([id, uses]) => ({ id, usedIn: [...uses].sort().join(', ') }));
}

export function extractLogicInventory(survey: Survey): LogicInventoryRow[] {
  const rows: LogicInventoryRow[] = [];

  for (const page of survey.pages) {
    for (const question of page.questions) {
      const cfIds = new Set<string>();
      if (question.skipLogics) {
        for (const sl of question.skipLogics) collectCustomFieldIdsFromLogic(sl.condition.filters, cfIds);
      }
      if (question.displayLogic) {
        collectCustomFieldIdsFromLogic(question.displayLogic.condition.filters, cfIds);
      }
      if (question.type === QUESTION_TYPE.MCQ || question.type === QUESTION_TYPE.RANK_ORDER) {
        for (const opt of question.options) {
          if (opt.answerLogic) collectCustomFieldIdsFromLogic(opt.answerLogic.condition.filters, cfIds);
        }
      }
      if ('statementRows' in question) {
        for (const row of question.statementRows) {
          if (row.answerLogic) collectCustomFieldIdsFromLogic(row.answerLogic.condition.filters, cfIds);
        }
      }
      if ('scaleColumns' in question) {
        for (const col of question.scaleColumns) {
          if (col.answerLogic) collectCustomFieldIdsFromLogic(col.answerLogic.condition.filters, cfIds);
        }
      }

      const hasSkip = Boolean(question.skipLogics?.length);
      const hasDisplay = Boolean(question.displayLogic);
      const hasAnswer = hasAnswerLogicOnQuestion(question);

      if (!hasSkip && !hasDisplay && !hasAnswer && cfIds.size === 0) continue;

      rows.push({
        questionId: question.id,
        skipLogics: hasSkip ? 'yes' : '—',
        displayLogic: hasDisplay ? 'yes' : '—',
        answerLogic: hasAnswer ? 'yes' : '—',
        customFieldRefs: cfIds.size ? [...cfIds].sort().join(', ') : '—',
      });
    }
  }

  return rows;
}

export function extractPlaceholderTokens(survey: Survey): PlaceholderRow[] {
  const tokenToQuestions = new Map<string, Set<string>>();

  const scanText = (text: string | undefined, questionId: string) => {
    if (!text) return;
    let match: RegExpExecArray | null;
    PLACEHOLDER_RE.lastIndex = 0;
    while ((match = PLACEHOLDER_RE.exec(text)) !== null) {
      const raw = match[1]?.trim();
      if (!raw) continue;
      const token = `{{${raw}}}`;
      const set = tokenToQuestions.get(token) ?? new Set<string>();
      set.add(questionId);
      tokenToQuestions.set(token, set);
    }
  };

  for (const page of survey.pages) {
    for (const question of page.questions) {
      scanText(question.questionText, question.id);
      scanText(question.questionDescription, question.id);
      if (question.displayVariants) {
        for (const v of question.displayVariants) scanText(v.questionText, question.id);
      }
    }
  }

  return [...tokenToQuestions.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([token, qids]) => {
      const key = token.startsWith('{{_c_')
        ? `CUSTOM_FIELD_VALUES['${token.slice(2, -2)}']`
        : token.startsWith('{{SURVEY_QUESTION')
          ? '(resolved in hook — render question.questionText as-is)'
          : `SURVEY_PLACEHOLDERS.${token.slice(2, -2)}`;
      return {
        token,
        key,
        questionIds: [...qids].sort().join(', '),
      };
    });
}

export function formatComponentTableRows(rows: ComponentInventoryRow[]): string {
  return rows
    .map((r) => {
      const block = r.blocking ? ' **blocking**' : '';
      return `| \`${r.componentFile}\` | ${r.requiredIf}${block} | |`;
    })
    .join('\n');
}

export function formatMatrixTableRows(rows: MatrixVariantRow[]): string {
  if (!rows.length) {
    return '| (no matrix questions in fetch) | | | | | | | | | | | | |';
  }
  return rows
    .map(
      (r) =>
        `| ${r.questionId} | ${r.type} | ${r.displayStyle} | ${r.gridLayout} | ${r.statementLayout} | ${r.transposeTable} | ${r.selectionMode} | ${r.hasNotApplicableOption} | ${r.branchOn} | ${r.scaleAnchorLabelCount} | ${r.matrixFormat} | ${r.nonNaM} |`,
    )
    .join('\n');
}

export function formatMcqOtherTableRows(rows: McqOtherInventoryRow[]): string {
  if (!rows.length) return '| (no MCQ Other options in fetch) | | | |';
  return rows
    .map((r) => `| ${r.questionId} | ${r.optionId} | ${r.contentType} | ${r.countryCodes} |`)
    .join('\n');
}

function isTruthyInventoryFlag(value: string | undefined): boolean {
  if (!value) return false;
  const v = value.trim().toLowerCase();
  return v === 'true' || v === 'yes';
}

/** CSAT/RATING/CFM matrix modes that use MatrixDropdown per row — no grid header band. */
export function isMatrixDropdownVariant(row: MatrixVariantRow): boolean {
  if (row.type === QUESTION_TYPE.CFM_MATRIX) {
    return /dropdown/i.test(row.gridLayout);
  }
  if (row.type === QUESTION_TYPE.CSAT_MATRIX || row.type === QUESTION_TYPE.RATING_MATRIX) {
    return (
      /dropdown|selectbox/i.test(row.displayStyle) || /dropdown/i.test(row.gridLayout)
    );
  }
  return false;
}

/** Header exclusivity hint for generated layout-tasks.md lines. */
function matrixHeaderExclusivityHint(row: MatrixVariantRow): string {
  if (isMatrixDropdownVariant(row)) return '';
  if (row.type === 'RATING_MATRIX' && /graphics/i.test(row.displayStyle)) return '';
  if (/transpose/i.test(row.transposeTable) && /true/i.test(row.transposeTable)) return '';
  return 'Header: one row — scaleAnchorLabels if §3c scaleAnchorLabelCount > 0, else scaleColumns[].label when showColumnHeaders; anchor XOR column — see shared.md §Matrix header orchestration';
}

/** Fetch-driven layout action line for one §3c matrix row. */
export function formatLayoutActionForVariant(row: MatrixVariantRow): string {
  const hasNa = isTruthyInventoryFlag(row.hasNotApplicableOption);
  const parts: string[] = [];

  if (isMatrixDropdownVariant(row)) {
    if (row.type === QUESTION_TYPE.CFM_MATRIX) {
      parts.push('question.gridLayout === "dropdown" → MatrixDropdown per row');
    } else if (/dropdown|selectbox/i.test(row.displayStyle)) {
      parts.push('question.displayStyle → MatrixDropdown per row with onPanelOpenChange');
    } else {
      parts.push('question.gridLayout === "dropdown" → MatrixDropdown per row with onPanelOpenChange');
    }
    parts.push('NO scale header row — labels inside MatrixDropdown options');
  } else if (row.type === 'CFM_MATRIX' && /bipolar/i.test(row.statementLayout)) {
    const nonNa = parseInt(row.nonNaM, 10);
    const isSlider =
      /SLIDER/i.test(row.matrixFormat) || (row.matrixFormat === '—' && Number.isFinite(nonNa) && nonNa > 7);
    if (isSlider) {
      parts.push(
        '3-zone flex 25% | flex-1 | 25% with oppositeStatementText + CustomSliderTrack in center (discrete): min=0, max=nonNaCols.length-1, ticks={nonNaCols.length}, positionMode="discrete"; anchors: columnPercent(i, n) — see cfm_matrix.md + scale-position-math.md §Bipolar SLIDER',
      );
    } else {
      parts.push('3-zone flex 25% | flex-1 | 25% with oppositeStatementText + radio grid in center zone');
    }
  } else if (/carousel/i.test(row.gridLayout)) {
    parts.push(
      'carousel: dedicated LikertMatrixCarouselLayout; statement once; scaleColumns[].label + radio per column (same m); chevrons + dots; never shouldShowMatrixStatementLabels in row grid',
    );
  } else if (row.type === 'RATING_MATRIX' && /graphics/i.test(row.displayStyle)) {
    parts.push(
      'GraphicsScaleZone on GraphicsColumnLabelsRow + every CustomSliderTrack row; graphicsLabelPercent + positionMode="continuous"; ticks={scaleColumns.length}; one label row outside statementRows.map; findIndex from stored column id — see rating_matrix.md',
    );
  } else if (/transpose/i.test(row.transposeTable) && /true/i.test(row.transposeTable)) {
    parts.push('transpose: scaleColumns=rows, statementRows=column headers');
  } else {
    const naSuffix = hasNa ? ' 56px' : '';
    parts.push(
      `grid cols = \`180px repeat(m, 1fr)${naSuffix}\`; cells loop nonNaScaleColumns + trailing NA when hasNA`,
    );
    if (/numbered/i.test(row.displayStyle)) {
      parts.push('numbered: displayIdx+1 badge in cell — not col.label');
    }
    if (/star|emoji|standard/i.test(row.displayStyle)) {
      parts.push('label i above cell i; anchors use % formula when scaleAnchorLabels set');
    }
  }

  if (hasNa && !isMatrixDropdownVariant(row) && !parts.some((p) => p.includes('NA'))) {
    parts.push('N/A trailing column in header + every row');
  }

  const headerHint = matrixHeaderExclusivityHint(row);
  if (headerHint && !parts.some((p) => p.includes('anchor XOR'))) {
    parts.push(headerHint);
  }

  return parts.join('; ');
}

export const MCQ_OTHER_TASKS_FILENAME = 'mcq-other-tasks.md';

export function formatMcqOtherTasksMarkdown(
  rows: McqOtherInventoryRow[],
  hasPickers: boolean,
): string {
  const lines = [
    '# MCQ Other tasks (blocking when pickers: yes)',
    '',
    '> Generated by `npx cfm-sdk sync-from-fetch`. Complete before `verify-interactions --from-fetch`.',
    '> Cookbook: [`layout-cookbook/mcq.md`](node_modules/@explorer02/cfm-survey-sdk/templates/docs/00-integration/layout-cookbook/mcq.md)',
    '',
  ];

  if (!hasPickers) {
    lines.push('No phone/postal/date Other options in fetch — picker components optional.');
    return `${lines.join('\n')}\n`;
  }

  lines.push('- [ ] **McqOtherCountrySelect.tsx** — PHONE_NUMBER + POSTAL_CODE');
  lines.push('- [ ] **McqOtherDatePicker.tsx** — DATE');
  lines.push('- [ ] **McqOtherInput.tsx** — branch on contentType; no `type="date"`; no country `<select>`');
  lines.push('- [ ] **package.json** — `react-international-phone`, `react-datepicker`, `date-fns`');
  lines.push('- [ ] **globals.css** — picker CSS imports');
  lines.push('');

  for (const row of rows) {
    if (!MCQ_OTHER_PICKER_CONTENT_TYPES.has(row.contentType)) continue;
    lines.push(
      `- [ ] **${row.questionId}** / option ${row.optionId} (${row.contentType})${row.countryCodes !== '—' ? ` — countries: ${row.countryCodes}` : ''}`,
    );
  }

  return `${lines.join('\n')}\n`;
}

export function formatLayoutTasksMarkdown(rows: MatrixVariantRow[]): string {
  if (!rows.length) {
    return `# Layout tasks (blocking)

> Generated by \`npx cfm-sdk sync-from-fetch\`. No matrix questions in fetch.

`;
  }

  const lines = [
    '# Layout tasks (blocking)',
    '',
    '> Generated by `npx cfm-sdk sync-from-fetch`. Complete before `npx cfm-sdk verify-layout` and Phase 5b (visual skin).',
    '> Cookbook: [`layout-cookbook.md`](node_modules/@explorer02/cfm-survey-sdk/templates/docs/00-integration/layout-cookbook.md)',
    '',
  ];

  for (const row of rows) {
    const branchLabel = row.branchOn ? `branch: ${row.branchOn}` : row.type;
    lines.push(
      `- [ ] **${row.questionId}** (${row.type} — ${branchLabel}): ${formatLayoutActionForVariant(row)}`,
    );
  }

  return `${lines.join('\n')}\n`;
}

export const LAYOUT_TASKS_FILENAME = 'layout-tasks.md';

export function formatCustomFieldTableRows(rows: CustomFieldRow[]): string {
  if (!rows.length) return '| (no _c_ ids in fetch) | | | | | |';
  return rows
    .map((r) => `| ${r.id} | | ${r.usedIn} | | | |`)
    .join('\n');
}

export function formatPlaceholderTableRows(rows: PlaceholderRow[]): string {
  if (!rows.length) return '| (no {{TOKEN}} in fetch) | | | |';
  return rows.map((r) => `| ${r.token} | ${r.key} | | ${r.questionIds} |`).join('\n');
}

export function formatLogicTableRows(rows: LogicInventoryRow[]): string {
  if (!rows.length) return '| (no logic flags in fetch) | | | | |';
  return rows
    .map(
      (r) =>
        `| ${r.questionId} | ${r.skipLogics} | ${r.displayLogic} | ${r.answerLogic} | ${r.customFieldRefs} |`,
    )
    .join('\n');
}
